<script>
import { mapActions, mapGetters } from 'vuex';
import IssueCard from '@mobile/views/Issues/Items/Issue.vue';
import Loader from '@mobile/components/Loader.vue';
import IssuesNavigation from '@mobile/views/Issues/components/Navigation';
import IssuesFilterModal from '@mobile/views/Issues/Modals/Filter.vue';
import AddIcon from '@mobile/components/Icons/AddIcon.vue';
import SearchBar from '@mobile/components/SearchBar.vue';
import BaseView from '@mobile/components/BaseView.vue';
import { ISSUE_STATUS, ISSUE_PERMISSIONS } from '@/constants';

export default {
    name: 'IssuesScreen',

    components: {
        BaseView,
        IssuesNavigation,
        IssueCard,
        Loader,
        IssuesFilterModal,
        AddIcon,
        SearchBar,
    },

    props: {
        status: {
            type: String,
            required: false,
            default: null,
            /*validator(status) {
                if (status === null) return true;
                return Object.entries(ISSUE_STATUS).find(
                    // eslint-disable-next-line no-unused-vars
                    ([key, value]) => value === status,
                );
            },*/
        },
    },

    data: () => ({
        isFiltersModalOpen: false,
        filters: {
            assignees: [],
            priority: null,
            status: null,
            sortBy: 'created_at',
            deadline: null,
            noAssignee: null,
            search: null,
            from: null,
            to: null,
        },
    }),

    computed: {
        ...mapGetters({
            issues: 'issues/issues',
            isInitialLoading: 'issues/isInitialLoading',
            isLoading: 'issues/isLoading',
            hasNextPage: 'issues/hasNextPage',
            filterOptions: 'issues/filterOptions',
            hasPermission: 'authentication/hasPermission',
        }),

        hasIssues() {
            return this.issues && this.issues.length > 0;
        },

        displaysLoadMoreButton() {
            return !this.isLoading && this.hasNextPage;
        },

        displaysAddIssueButton() {
            return this.hasPermission(ISSUE_PERMISSIONS.CREATE_PERMISSION_NAME);
        },

        emptyMessage() {
            return this.status === ISSUE_STATUS.SOLVED
                ? this.$t('There are no solved issues')
                : this.$t('There are no open issues');
        },
    },

    created() {
        this.filters = {
            ...this.filters,
            status: this.status,
        };

        this.issuesScreenCreated({
            filters: this.filters,
        });
    },

    methods: {
        ...mapActions({
            issuesScreenCreated: 'issues/issuesScreenCreated',
            issueScreenRefreshed: 'issues/issueScreenRefreshed',
            loadMoreButtonClicked: 'issues/loadMoreButtonClicked',
            filterRequested: 'issues/filterRequested',
        }),

        issueClicked(issue) {
            this.$router.push({
                name: 'issues-edit',
                params: { id: issue.id },
            });
        },

        fliterButtonClicked() {
            this.isFiltersModalOpen = true;
        },

        flitersModalClosed() {
            this.isFiltersModalOpen = false;
        },

        filtersModalUpdated(filters) {
            this.filters = { ...filters };
            if (this.status === ISSUE_STATUS.SOLVED) { this.filters.status = ISSUE_STATUS.SOLVED; }
            this.filterRequested({ filters: this.filters });
        },

        searchTermChanged(search) {
            this.filters.search = search;
            this.filterRequested({ filters: this.filters });
        },

        refreshIssues() {
            return this.issueScreenRefreshed({
                assignees: [],
                priority: null,
                status: null,
                sortBy: 'created_at',
                deadline: null,
                noAssignee: null,
                search: null,
                from: null,
                to: null,
            });
        },
    },
};
</script>

<template>
    <BaseView>
        <template v-slot:top>
            <IssuesNavigation />
            <SearchBar
                @search="searchTermChanged"
                @filter="fliterButtonClicked"
            />
        </template>

        <div v-if="isInitialLoading" class="middle-of-page flex-col text-80">
            <Loader />
        </div>

        <template v-else-if="hasIssues">
            <div v-pull-to-refresh="refreshIssues">
                <IssueCard
                    v-for="issue in issues"
                    :key="issue.id"
                    :title="issue.title"
                    :payload="issue.payload"
                    :status="issue.status"
                    :priority="issue.priority"
                    :due-at="issue.dueAt"
                    :resolved-at="issue.resolvedAt"
                    :assignees="
                        issue.assignees ? Object.values(issue.assignees) : []
                    "
                    :resolved-by="issue.resolvedBy"
                    @click.native="issueClicked(issue)"
                />

                <div class="flex justify-center">
                    <div v-show="isLoading" class="p-4 flex-col text-80">
                        <Loader />
                    </div>

                    <button
                        v-show="displaysLoadMoreButton"
                        class="btn btn-default btn-primary main-button mx-4"
                        type="text"
                        @click="loadMoreButtonClicked(filters)"
                    >
                        {{ $t("Load more") }}
                    </button>
                </div>
            </div>
        </template>

        <div v-else class="middle-of-page">
            <div v-pull-to-refresh="refreshIssues">{{ emptyMessage }}</div>
        </div>

        <router-link
            v-if="displaysAddIssueButton"
            class="fab-button"
            :to="{ name: 'issues-raise' }"
            ><AddIcon
        /></router-link>

        <template v-slot:bottom>
            <bottom-navigation />
        </template>

        <portal to="modals">
            <transition name="fade">
                <IssuesFilterModal
                    v-if="isFiltersModalOpen"
                    :filters="filters"
                    :filter-options="filterOptions"
                    :status="status"
                    @update="filtersModalUpdated"
                    @close="flitersModalClosed"
                />
            </transition>
        </portal>
    </BaseView>
</template>
